import { getLocaleInfo } from '../../lang/locales'
import { MapboxSearchSuggestion } from './types'

export class MapboxSearchAPI {
  langCode = 'en'
  sessionToken: string
  userCoordinates: UserCoordinates
  distanceUnit?: string

  constructor(
    langCode: string,
    sessionId: string,
    userCoordinates?: UserCoordinates,
  ) {
    if (!sessionId) {
      throw new Error(
        'MapboxSearchAPI: sessionId param missing from constructor',
      )
    }
    this.langCode = langCode
    this.sessionToken = sessionId
    this.distanceUnit = getLocaleInfo(langCode)?.distanceUnit
    if (userCoordinates && userCoordinates.lat && userCoordinates.lng) {
      this.userCoordinates = userCoordinates
    }
  }

  async get(input: string): Promise<MapboxSearchSuggestion[]> {
    const url = this.getUrl(input)
    // TODO: Add proper types for Mapbox suggest API response
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const response = await $fetch<any>(url, {
      timeout: 10000,
    })
    return this.parseResponse(response)
  }

  private getUrl(query: string): string {
    if (!query) {
      console.error('MapboxSearchAPI called with empty query')
    }

    const params = new URLSearchParams({
      q: query,
      session_token: this.sessionToken,
      language: this.langCode,
    })

    if (this.userCoordinates) {
      params.set('lng', String(this.userCoordinates.lng))
      params.set('lat', String(this.userCoordinates.lat))
    }

    return `/api/mb/suggest/?${params.toString()}`
  }

  // TODO: Add proper types for Mapbox suggest API response
  private parseResponse(body: any): MapboxSearchSuggestion[] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!body.suggestions) return []
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return body.suggestions.map((suggestion: unknown) =>
      this.mapRow(suggestion),
    )
  }

  // TODO: Add proper types for Mapbox suggestion object
  private mapRow(suggestion: any): MapboxSearchSuggestion {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      name: suggestion.name,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      placeFormatted: suggestion.place_formatted,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      mapboxId: suggestion.mapbox_id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      distance: suggestion.distance
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          this.metersToUserUnit(suggestion.distance)
        : undefined,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      poiCategories: suggestion.poiCategories,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      countryCode: suggestion.countryCode,
    }
  }

  private metersToUserUnit(meters) {
    if (this.distanceUnit === 'km') {
      return metersToKM(meters)
    }
    return metersToMiles(meters)
  }

  async getPointForSuggestion(mapboxId: string) {
    const params = new URLSearchParams({
      session_token: this.sessionToken,
    })
    // TODO: Add proper types for Mapbox retrieve API response
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const response = await $fetch<any>(
      `/api/mb/retrieve/${mapboxId}?${params.toString()}`,
      {
        timeout: 10000,
      },
    )
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return response
  }
}

function metersToMiles(meters) {
  const meterToMileRatio = 1609.344
  return Math.round(meters / meterToMileRatio)
}

function metersToKM(meters) {
  return Math.round(meters / 1000)
}

interface UserCoordinates {
  lat: number
  lng: number
}
